import React from 'react';
import { useTheme } from 'styled-components';
import { Box, Button, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import {
  COMPANION_PLAN_CATEGORY_IDENTIFIER,
  MAXIMUM_COMPANION_QUANTITY,
} from 'components/shop/MobilePlans/constants/plans.constants';
import { useSelectedPlans } from 'components/shop/MobilePlans/providers';
import {
  EligibilityCount,
  MobilePlansDetails,
  SelectedPlan,
} from 'components/shop/MobilePlans/typings/plans';
import { useTrackActionLayerView } from '../../hooks/useTrackActionLayerView';
import PlanQuantitySelector from '../PlanQuantitySelector';
import EligibilityInfoBanner from './EligibilityInfoBanner';

interface SelectQuantityStepProps {
  planCardsProducts: MobilePlansDetails;
  selectedPlan: SelectedPlan;
  isAddingBundles: boolean;
  nextButtonText?: string;
  eligibilityCount?: EligibilityCount;
  onNext(): void;
  onCancel(): void;
}

interface IsCompanionOfferProps {
  offerId: string;
  planCardsProducts: MobilePlansDetails;
}

interface GetMaxQuantityProps extends IsCompanionOfferProps {
  eligibilityCount?: EligibilityCount;
}

const isCompanionOffer = ({
  offerId,
  planCardsProducts,
}: IsCompanionOfferProps) =>
  !!planCardsProducts
    ?.find(({ planDetails }) => planDetails.planId === offerId)
    ?.planDetails.categoryIdentifiers?.includes(
      COMPANION_PLAN_CATEGORY_IDENTIFIER,
    );

const getMaxQuantity = ({
  offerId,
  planCardsProducts,
  eligibilityCount,
}: GetMaxQuantityProps) => {
  if (eligibilityCount) {
    return eligibilityCount.available;
  }
  if (isCompanionOffer({ offerId, planCardsProducts })) {
    return MAXIMUM_COMPANION_QUANTITY;
  }

  return undefined;
};

const SelectQuantityStep = ({
  planCardsProducts,
  selectedPlan,
  isAddingBundles,
  nextButtonText = 'Add to cart',
  eligibilityCount,
  onNext,
  onCancel,
}: SelectQuantityStepProps) => {
  const {
    palette: { baseColors },
  } = useTheme();
  const { increaseSelectedPlan, decreaseSelectedPlan } = useSelectedPlans();

  useTrackActionLayerView({ planDetails: planCardsProducts });

  const { quantity, offerId, planName, isTeamUpEligible } = selectedPlan;

  const maxQuantity = getMaxQuantity({
    offerId,
    planCardsProducts,
    eligibilityCount,
  });

  return (
    <LinkRegion regionId="selectedPlansActionLayer">
      <Text variant="bodySDemi" color={baseColors.mawheranui}>
        Selected plan
      </Text>
      <Text variant="bodyMDemi" mb="s4">
        {planName}
      </Text>

      <Text variant="bodyMDemi" mb="s3">
        How many of this plan would you like to add?
      </Text>

      <PlanQuantitySelector
        quantity={quantity}
        onIncrease={() =>
          increaseSelectedPlan(offerId, planName, !!isTeamUpEligible)
        }
        onDecrease={() => decreaseSelectedPlan(offerId, planName)}
        disabled={isAddingBundles}
        suffix="selected"
        maxQuantity={maxQuantity}
      />

      {eligibilityCount && (
        <EligibilityInfoBanner
          planName={planName}
          eligibilityCount={eligibilityCount}
        />
      )}

      <Box flexDirection="column" gap="s2">
        <Button
          variant="primary"
          onClick={onNext}
          isLoading={isAddingBundles}
          fullWidth
        >
          {nextButtonText}
        </Button>
        <Button
          variant="default"
          onClick={onCancel}
          disabled={isAddingBundles}
          fullWidth
        >
          Cancel
        </Button>
      </Box>
    </LinkRegion>
  );
};

export default SelectQuantityStep;
